import {
  getPricingPlansSettings as publicGetPricingPlansSettings,
  updatePricingPlansSettings as publicUpdatePricingPlansSettings,
} from './pricing-plans-settings-v1-pricing-plans-settings-pricing-plans-settings.public.js';
import { createRESTModule } from '@wix/sdk-runtime/rest-modules';
import { createEventModule } from '@wix/sdk-runtime/event-definition-modules';
import {
  BuildRESTFunction,
  MaybeContext,
  BuildEventDefinition,
} from '@wix/sdk-types';
import { onPricingPlansSettingsUpdated as publicOnPricingPlansSettingsUpdated } from './pricing-plans-settings-v1-pricing-plans-settings-pricing-plans-settings.public.js';
import { onPricingPlansSettingsCreated as publicOnPricingPlansSettingsCreated } from './pricing-plans-settings-v1-pricing-plans-settings-pricing-plans-settings.public.js';

export const getPricingPlansSettings: MaybeContext<
  BuildRESTFunction<typeof publicGetPricingPlansSettings> &
    typeof publicGetPricingPlansSettings
> = /*#__PURE__*/ createRESTModule(publicGetPricingPlansSettings);
export const updatePricingPlansSettings: MaybeContext<
  BuildRESTFunction<typeof publicUpdatePricingPlansSettings> &
    typeof publicUpdatePricingPlansSettings
> = /*#__PURE__*/ createRESTModule(publicUpdatePricingPlansSettings);
/**
 * Triggered when a site's Pricing Plans settings are updated.
 */
export const onPricingPlansSettingsUpdated: BuildEventDefinition<
  typeof publicOnPricingPlansSettingsUpdated
> &
  typeof publicOnPricingPlansSettingsUpdated = /*#__PURE__*/ createEventModule(
  publicOnPricingPlansSettingsUpdated
);
/**
 * Triggered when a site's Pricing Plans settings are created, which happens when the Wix Pricing Plans app is installed on the site.
 */
export const onPricingPlansSettingsCreated: BuildEventDefinition<
  typeof publicOnPricingPlansSettingsCreated
> &
  typeof publicOnPricingPlansSettingsCreated = /*#__PURE__*/ createEventModule(
  publicOnPricingPlansSettingsCreated
);

export {
  TaxableAddress,
  WebhookIdentityType,
  FeaturePeriod,
  ContractSwitchClassification,
  ProviderName,
  IntervalUnit,
  SubscriptionStatus,
  ReactivationReasonEnum,
  Initiator,
  RefundType,
  UnassignReason,
  ContractSwitchType,
  ContractSwitchReason,
  PriceIncreaseTrigger,
  ProductAdjustment,
  RecurringChargeFailureReason,
  State,
  SiteCreatedContext,
  Namespace,
  DeleteStatus,
} from './pricing-plans-settings-v1-pricing-plans-settings-pricing-plans-settings.universal.js';
export {
  PricingPlansSettings,
  ExtendedFields,
  GuestCheckoutSettings,
  InvoiceSettings,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  RestoreInfo,
  EntityUpdatedEvent,
  EntityDeletedEvent,
  ActionEvent,
  MessageEnvelope,
  IdentificationData,
  IdentificationDataIdOneOf,
  AccountInfo,
  GetPricingPlansSettingsRequest,
  GetPricingPlansSettingsResponse,
  UpdatePricingPlansSettingsRequest,
  UpdatePricingPlansSettingsResponse,
  FeatureEvent,
  FeatureEventEventOneOf,
  FeatureEnabled,
  FeatureEnabledReasonOneOf,
  Feature,
  FeatureQuantityInfoOneOf,
  FeatureContext,
  BooleanFeature,
  QuotaFeature,
  QuotaInfo,
  QuotaInfoEntry,
  TransferredFromAnotherAccountReason,
  ReassignedFromSiteReason,
  AssignedFromFloatingReason,
  NewFeatureReason,
  ContractSwitchedReason,
  ManualFeatureCreationReason,
  MigratedFromLegacyReason,
  ManualSyncReason,
  FeatureDisabled,
  FeatureDisabledReasonOneOf,
  UnAssingedToFloatingReason,
  ReplacedByAnotherSubscriptionReason,
  ReassignedToAnotherSiteReason,
  FeatureUpdated,
  FeatureUpdatedPreviousQuantityInfoOneOf,
  FeatureUpdatedReasonOneOf,
  FeatureCancelled,
  FeatureCancelledReasonOneOf,
  TransferredToAnotherAccountReason,
  CancelRequestedReason,
  QuotaExhaustedReason,
  FeatureQuotaUsageChanged,
  Empty,
  SubscriptionEvent,
  SubscriptionEventEventOneOf,
  SubscriptionCreated,
  Subscription,
  BillingReference,
  Cycle,
  CycleCycleSelectorOneOf,
  Interval,
  OneTime,
  ReactivationData,
  SubscriptionAssigned,
  SubscriptionCancelled,
  CancellationDetails,
  SubscriptionAutoRenewTurnedOn,
  SubscriptionAutoRenewTurnedOff,
  SubscriptionUnassigned,
  SubscriptionTransferred,
  RecurringChargeSucceeded,
  ContractSwitched,
  ProductPriceIncreaseData,
  SubscriptionNearEndOfPeriod,
  SubscriptionPendingChange,
  RecurringChargeAttemptFailed,
  SubscriptionUpdated,
  SubscriptionMigratedFromVista,
  SubscriptionVistaMigrationReverted,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  Asset,
  SiteCreated,
  SiteTransferred,
  SiteDeleted,
  DeleteContext,
  SiteUndeleted,
  SitePublished,
  SiteUnpublished,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  ServiceProvisioned,
  ServiceRemoved,
  SiteRenamed,
  SiteHardDeleted,
  NamespaceChanged,
  StudioAssigned,
  StudioUnassigned,
  SiteUrlChanged,
  SitePurgedExternally,
  OdeditorAssigned,
  OdeditorUnassigned,
  PicassoAssigned,
  PicassoUnassigned,
  WixelAssigned,
  WixelUnassigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  UserDomainMediaEnabled,
  UserDomainMediaDisabled,
  EditorlessAssigned,
  EditorlessUnassigned,
  BaseEventMetadata,
  EventMetadata,
  AccountInfoMetadata,
  PricingPlansSettingsUpdatedEnvelope,
  PricingPlansSettingsCreatedEnvelope,
} from './pricing-plans-settings-v1-pricing-plans-settings-pricing-plans-settings.universal.js';
export {
  TaxableAddressWithLiterals,
  WebhookIdentityTypeWithLiterals,
  FeaturePeriodWithLiterals,
  ContractSwitchClassificationWithLiterals,
  ProviderNameWithLiterals,
  IntervalUnitWithLiterals,
  SubscriptionStatusWithLiterals,
  ReactivationReasonEnumWithLiterals,
  InitiatorWithLiterals,
  RefundTypeWithLiterals,
  UnassignReasonWithLiterals,
  ContractSwitchTypeWithLiterals,
  ContractSwitchReasonWithLiterals,
  PriceIncreaseTriggerWithLiterals,
  ProductAdjustmentWithLiterals,
  RecurringChargeFailureReasonWithLiterals,
  StateWithLiterals,
  SiteCreatedContextWithLiterals,
  NamespaceWithLiterals,
  DeleteStatusWithLiterals,
} from './pricing-plans-settings-v1-pricing-plans-settings-pricing-plans-settings.universal.js';
